import React, { useState, useMemo, useEffect, useCallback, useRef, ReactElement } from 'react';
import { PieChartData } from '@Types/nivo';
import { OVERVIEW_DEFAULT_TABS } from '@Constants/constants';
import Notification from '@Common/Notification';
import { useLoadLocationsByProjects, useGetOverviewTab, DashboardSetting, OverviewLocationFilters } from '@Apis/companyDashboard';
import { useLoadLocationDetails } from '@Apis/locationDashboard';
import HeaderTile from './Tiles/HeaderTile';
import ByAttributeTile from './Tiles/ByAttributeTile';
import { LocationDetailData, LocationSummaryData } from '../types';
import LocationsList from './LocationsList';
import { BreakdownSelection } from './breakdownSelection';
import LocationDetailTile, { DEFAULT_LOCATION_DETAIL_TAB_ID, TASKS_LOCATION_DETAIL_TAB_ID } from './Tiles/LocationDetailTile';
import LocationDetailBreadcrumb from './Tiles/LocationDetailBreadcrumb';
import './Openings.scss';

interface OpeningsProps {
    projectIds: number[];
    selectedSectionIds?: string[];
    dashboardSettings?: DashboardSetting;
}

const Openings = ({ projectIds, selectedSectionIds = [], dashboardSettings }: OpeningsProps): ReactElement => {
    // When selectedSectionIds is empty (legacy config or API not yet returning the field),
    // default to showing all 3 existing sections so the tab is never blank.
    const showAll = selectedSectionIds.length === 0;
    const show = (id: string) => showAll || selectedSectionIds.includes(id);

    const [selectedLocationKey, setSelectedLocationKey] = useState<string | null>(null);
    const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);

    // Non-null only while the sidebar is showing a group the user drilled into from a
    // breakdown panel. It is what puts the breadcrumb and the back arrow on the sidebar, so
    // every path that selects a plain location row has to clear it.
    const [breakdownSelection, setBreakdownSelection] = useState<BreakdownSelection | null>(null);

    // Held here rather than inside the tile, the same way LocationsList owns the breakdown
    // panel's view: picking a different location keeps the sub-tab the user was reading, and
    // the inline tile and the mobile drawer stay on the same one.
    const [detailTabId, setDetailTabId] = useState<number>(DEFAULT_LOCATION_DETAIL_TAB_ID);

    // Read by the drill-down callbacks, which must not take `detailTabId` as a dependency:
    // they are handed to LocationsList, and a fresh identity on every tab change would
    // re-render every row in the list.
    const detailTabIdRef = useRef(detailTabId);
    useEffect(() => { detailTabIdRef.current = detailTabId; }, [detailTabId]);

    // Non-null only while drilled in: the tab that was open before the drill-down forced the
    // sidebar onto Tasks. Restored on the way out, so a drill-down cannot strand the user on
    // a tab that has nothing to show outside one.
    const preDrillTabIdRef = useRef<number | null>(null);

    const locationFilters: OverviewLocationFilters = useMemo(() => ({
        projectIds: dashboardSettings?.projectIds?.length ? dashboardSettings.projectIds : projectIds,
        brandIds: dashboardSettings?.brandIds ?? [],
        areaIds: dashboardSettings?.areaIds ?? [],
        regionIds: dashboardSettings?.regionIds ?? [],
        managementCompanyIds: dashboardSettings?.managementCompanyIds ?? [],
        locationTypeIds: dashboardSettings?.locationTypeIds ?? [],
        continentIds: dashboardSettings?.continentIds ?? [],
        locationIds: dashboardSettings?.locationIds ?? [],
        tagIds: dashboardSettings?.tagIds ?? [],
        disciplineIds: dashboardSettings?.disciplineIds ?? [],
        departmentIds: dashboardSettings?.departmentIds ?? [],
        communityIds: dashboardSettings?.communityIds ?? [],
        phaseIds: dashboardSettings?.phaseIds ?? [],
        baselineIds: dashboardSettings?.baselineIds ?? [],
        isMilestonesOnly: dashboardSettings?.isMilestonesOnly ?? false,
    }), [projectIds, dashboardSettings]);

    // ---- Pie chart data: locations by projects ----
    const { data: locationsByProjectsData, error: locationsByProjectsError } = useLoadLocationsByProjects(locationFilters);

    const openingsByBrand = (locationsByProjectsData?.brand ?? []) as unknown as PieChartData[];
    const openingsByArea = (locationsByProjectsData?.area ?? []) as unknown as PieChartData[];
    const openingsByRegion = (locationsByProjectsData?.region ?? []) as unknown as PieChartData[];
    const openingsByType = (locationsByProjectsData?.type ?? []) as unknown as PieChartData[];
    const openingsByMgmtCo = (locationsByProjectsData?.managementCompany ?? []) as unknown as PieChartData[];

    // ---- Locations list from overview tab (includes metrics, filtered by dashboard settings) ----
    // `isLoading`, never `isFetching`: this feeds a `hideWhileLoading` indicator in the tile,
    // and the query key is the serialized filters, so `isFetching` goes true again on every
    // filter tweak and on refetch-on-mount/focus — which would blank the graph out of every
    // visible row mid-scroll. `isLoading` is only true while there is nothing to show yet.
    const { data: overviewTabData, error: overviewTabError, isLoading: isLoadingMetrics } =
        useGetOverviewTab(OVERVIEW_DEFAULT_TABS.Openings.id, locationFilters);

    // Memoized so the array identity is stable across renders — LocationsList defaults the
    // selection to the first item whenever the `locations` reference changes, so an unstable
    // reference would snap the selection back to the first location on every re-render.
    // Metrics ride along the response via schema passthrough, so cast to the richer type.
    // A location with no tasks in scope (filtered out by the dashboard settings, or never
    // accessible to this user) has nothing to report, so it is dropped from the list rather
    // than rendered as a row of zeroes. `totalTaskCount` is only trusted once it arrives —
    // while metrics are still loading it is undefined, and hiding on that would make the
    // whole list blink out on every refetch.
    const locations = useMemo(
        () => ((overviewTabData?.locations ?? []) as unknown as LocationSummaryData[])
            .filter((location) => location.totalTaskCount !== 0),
        [overviewTabData]
    );

    // ---- Location detail (keyed by selected locationKey) ----
    const { data: locationDetailsData, error: locationDetailsError } = useLoadLocationDetails(selectedLocationKey);

    // Surface load failures to the user. The old Redux path showed a Notification on each
    // failure; the react-query hooks expose errors via `error`, so mirror that here.
    useEffect(() => {
        if (locationsByProjectsError) {
            Notification('error', 'Error', 'An error occurred while loading location attribute data.');
        }
    }, [locationsByProjectsError]);

    useEffect(() => {
        if (overviewTabError) {
            Notification('error', 'Error', 'An error occurred while loading openings data.');
        }
    }, [overviewTabError]);

    useEffect(() => {
        if (locationDetailsError) {
            Notification('error', 'Error', 'An error occurred while loading location details.');
        }
    }, [locationDetailsError]);

    // Build the activeLocationDetail by merging server response with the selected locationKey.
    // The details-filters endpoint does not echo back locationKey, so we inject it here.
    const activeLocationDetail: LocationDetailData | null =
        locationDetailsData && selectedLocationKey
            ? ({ ...locationDetailsData.location, locationKey: selectedLocationKey } as LocationDetailData)
            : null;

    // Every way out of a drill-down runs through here: the breadcrumb's back arrow, the
    // header's close, and picking any row in the list.
    const leaveDrillDown = useCallback(() => {
        if (preDrillTabIdRef.current === null) return;
        setDetailTabId(preDrillTabIdRef.current);
        preDrillTabIdRef.current = null;
        setBreakdownSelection(null);
    }, []);

    // Drilling into a group on a row that is not the selected one starts a fresh details
    // request, and `useLoadLocationDetails` has no placeholder data, so `activeLocationDetail`
    // is null until it resolves. The tile cannot render without a location, but the breadcrumb
    // can — and it carries the back arrow, so without this the drill-down would be unescapable
    // for the length of the request.
    const isLoadingDrillDown = breakdownSelection !== null && activeLocationDetail === null;

    // Memoized so LocationsList's effect can safely depend on it without re-running on every render.
    const onLocationChange = useCallback((id: number) => {
        leaveDrillDown();
        const location = locations.find(loc => loc.id === id);
        if (!location) {
            setSelectedLocationKey(null);
            return;
        }
        setSelectedLocationKey(location.locationKey);
        setIsDetailDrawerOpen(true);
    }, [locations, leaveDrillDown]);

    // Drilling into a breakdown row is a request to see that group's tasks, so it opens the
    // sidebar on the Tasks tab regardless of which tab was last remembered. The displaced tab
    // is stashed rather than overwritten: outside a drill-down the Tasks tab has no group to
    // scope to, so leaving the user parked on it would both strand them on an empty panel and
    // lose the tab they were reading.
    const onBreakdownGroupSelect = useCallback((selection: BreakdownSelection) => {
        if (preDrillTabIdRef.current === null) {
            // Tasks is a normal entry in the tab strip, so it can already be the open tab when
            // the drill starts. Stashing it would hand the user straight back to a panel that
            // reads "No tasks selected" the moment they leave the drill-down.
            preDrillTabIdRef.current = detailTabIdRef.current === TASKS_LOCATION_DETAIL_TAB_ID
                ? DEFAULT_LOCATION_DETAIL_TAB_ID
                : detailTabIdRef.current;
        }
        setBreakdownSelection(selection);
        setSelectedLocationKey(selection.locationKey);
        setDetailTabId(TASKS_LOCATION_DETAIL_TAB_ID);
        setIsDetailDrawerOpen(true);
    }, []);

    // The back arrow / close button on a drilled-in sidebar. It leaves the drill-down, not the
    // location: LocationsList owns the row highlight and gets no reset signal, so dropping the
    // selection here would leave the sidebar reading "Select a location" under a row that is
    // still visibly selected. The sidebar falls back to the plain location detail instead, and
    // the breakdown panel — also owned by LocationsList — stays expanded behind it.
    const onBreakdownBack = useCallback(() => {
        leaveDrillDown();
        setIsDetailDrawerOpen(false);
    }, [leaveDrillDown]);

    return (
        <div className='overview-openings'>
            {show('recentAndUpcomingOpenings') && (
                <div className='row g-3'>
                    <div className='col-md-6 col-lg-2'>
                        <HeaderTile title='Opened in past 90 days' text={'56'}></HeaderTile>
                    </div>
                    <div className='col-md-12 col-lg-6 col-xl-6'>
                        <HeaderTile title='Openings by Month'></HeaderTile>
                    </div>
                    <div className='col-md-6 col-lg-4'>
                        <HeaderTile title='Milestones Completed'></HeaderTile>
                    </div>
                </div>
            )}

            {show('locationsBreakdownByAttribute') && (
                <div className='row g-3'>
                    <div className='col-sm-12 section-header section-header'>Number of Location/Teams by Attribute</div>

                    <div className='col-sm-12 by-attribute-tiles'>
                        <ByAttributeTile title='Brand' data={openingsByBrand} />
                        <ByAttributeTile title='Area' data={openingsByArea} />
                        <ByAttributeTile title='Region' data={openingsByRegion} />
                        <ByAttributeTile title='Type' data={openingsByType} />
                        <ByAttributeTile title='Mgmt Co' data={openingsByMgmtCo} />
                    </div>
                </div>
            )}

            {show('locationsSummary') && (
                <>
                    <div className='row'>
                        <div className='col-sm-12 section-header'>
                            Locations
                        </div>
                    </div>

                    <div className='row g-3'>
                        <div className='col-12 col-lg-8'>
                            <LocationsList
                                locations={locations}
                                onLocationChange={onLocationChange}
                                onBreakdownGroupSelect={onBreakdownGroupSelect}
                                isLoadingMetrics={isLoadingMetrics}
                                filters={locationFilters}
                            ></LocationsList>
                        </div>

                        {/* Inline detail panel — hidden on mobile (shown as a drawer instead) */}
                        <div className='col-12 col-lg-4 location-detail-inline'>
                            {activeLocationDetail && (
                                <LocationDetailTile
                                    location={activeLocationDetail}
                                    projectIds={locationFilters.projectIds}
                                    activeTabId={detailTabId}
                                    onTabChange={setDetailTabId}
                                    breakdownSelection={breakdownSelection}
                                    onBack={onBreakdownBack} />
                            )}
                            {isLoadingDrillDown && breakdownSelection && (
                                <div className='location-detail-tile'>
                                    <LocationDetailBreadcrumb
                                        selection={breakdownSelection}
                                        current='group'
                                        onBack={onBreakdownBack}
                                    />
                                    <div className='location-detail-placeholder'>Loading location details…</div>
                                </div>
                            )}
                            {!activeLocationDetail && !isLoadingDrillDown && (
                                <div className='location-detail-placeholder'>Select a location to view details</div>
                            )}
                        </div>
                    </div>

                    {/* Mobile-only slide-in drawer for the location detail. Also opens while a
                        drill-down's details request is in flight so the user gets immediate
                        feedback and a back control instead of a drawer that never appears. */}
                    <div
                        className={`location-detail-backdrop ${isDetailDrawerOpen && (activeLocationDetail || isLoadingDrillDown) ? 'is-open' : ''}`}
                        onClick={() => setIsDetailDrawerOpen(false)}
                    />
                    <div className={`location-detail-drawer ${isDetailDrawerOpen && (activeLocationDetail || isLoadingDrillDown) ? 'is-open' : ''}`}>
                        <div className='location-detail-drawer-header'>
                            <button
                                type='button'
                                className='location-detail-drawer-close'
                                aria-label='Close location details'
                                onClick={() => setIsDetailDrawerOpen(false)}>
                                <i className='fas fa-xmark'></i>
                            </button>
                        </div>
                        <div className='location-detail-drawer-body'>
                            {activeLocationDetail && (
                                <LocationDetailTile
                                    location={activeLocationDetail}
                                    projectIds={locationFilters.projectIds}
                                    activeTabId={detailTabId}
                                    onTabChange={setDetailTabId}
                                    breakdownSelection={breakdownSelection}
                                    onBack={onBreakdownBack} />
                            )}
                            {isLoadingDrillDown && breakdownSelection && (
                                <div className='location-detail-tile'>
                                    <LocationDetailBreadcrumb
                                        selection={breakdownSelection}
                                        current='group'
                                        onBack={onBreakdownBack}
                                    />
                                    <div className='location-detail-placeholder'>Loading location details…</div>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Openings;
